//setHashes

import { useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { store } from 'components/Store';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import ChangeSearchQuery from 'components/Dashboard/utilities/changeSearchQuery';
import { setFilterHashes } from './filtersHashes';
import ConfigData from 'config/ConfigData';
import uppercaseFirstLetter from 'components/Dashboard/utilities/uppercaseFirstLetter';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import dayjs from 'dayjs';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import { useTypeOfUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';

const useCompileFiltersFunction = (section) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { search, referenceState, activePlan } = state;
  const { enableSentiment } = activePlan ?? {};
  const { activeReference } = referenceState ?? {};
  const { activeSearch, list } = search ?? {};

  const params = useParams();
  const location = useLocation();
  const { inboxSection, referenceSection, searchResultsSection } = useMainSectionParameter(params);
  const { newAgencyPlan } = useTopicOrClientWord();
  const { isParliamentarian } = useTypeOfUsers();

  const compileFiltersFunction = (filtersToApply) => {
    let sort,
      searchWithin,
      filters = [],
      unread,
      hash,
      sortMode,
      singleSelectValues = {},
      phrase = [];
    //sort
    let sortModeFilter = filtersToApply.find((option) => option.type === 'sortMode');
    let sortFilter = filtersToApply.find(
      (option) => option.type === 'sort' && option.name !== 'Nation' && option.name !== 'Update type'
    );

    // Nations
    let nationsFilter = filtersToApply.find((option) => option.name === 'Nation');

    //unread
    let unreadFilter = filtersToApply.find((option) => option.name === 'See unread only');

    //dateFilters
    let dateFilter = filtersToApply.find((option) => option.type === 'date');
    if (dateFilter && dateFilter.optionSelected !== '') {
      let dateItem = dateFilter.options.find((item) => item.name === dateFilter.optionSelected);
      dateItem.filter.forEach((item) => {
        if (typeof item === 'object') {
          filters.push(item);
        } else {
          filters.push({
            field: dateFilter.filterName,
            value: item.replace('Z', ''),
            operator: 'DATE_GTE',
          });
        }
      });
      //PHRASE
      if (dateItem.showCalendar) {
        let dates = dateItem.dates.split(',');
        phrase.push(
          dateItem.name === 'Custom'
            ? `${dayjs(dates[0]).format('D MMM')} - ${dayjs(dates[1]).format('D MMM')}`
            : `${dateItem.name.toLowerCase()} ${dayjs(dates[0]).format('D MMM')}`
        );
      } else {
        phrase.push(dateItem.name.toLowerCase());
      }
    }

    //searchFilter
    const searchFilter = filtersToApply.find((option) => option.type === 'search');
    const toggleFiltersAll = filtersToApply.filter(
      (option) => option.type === 'toggle' && option.name !== 'See unread only'
    );
    const groupByDocketToggle = filtersToApply.find(
      (option) => option.type === 'toggle' && option.name === 'Group by docket'
    );
    //parliamentary record filters
    const parliamentaryFilters = filtersToApply.find(
      (option) => option.type === 'parliamentaryRecordMultipleHierarchy'
    );

    //topicPicker filter
    const topicPickerFilter = filtersToApply.find((option) => option.type === 'topicPickerWithSentiment');
    const singleSelectFilters = filtersToApply.filter((option) => option.type === 'singleSelect');
    singleSelectFilters.forEach((item) => {
      if (item?.filterName) {
        singleSelectValues[item.filterName] = item.optionSelected;
      }
      const selectedOption = item.options?.find((option) => option.name === item.optionSelected);
      if (Array.isArray(selectedOption?.filter)) {
        selectedOption.filter.forEach((filterItem) => {
          if (
            typeof filterItem.value !== 'undefined' &&
            typeof filterItem.field !== 'undefined' &&
            typeof filterItem.operator !== 'undefined'
          ) {
            filters.push(filterItem);
          }
        });
      }
    });

    //normalFilters
    let filtersToCompile = filtersToApply.filter(
      (option) => !option.type || option.type === 'membership' || option.filterName === 'tags'
    );

    //compile sort
    if (sortFilter) {
      let sortOptions = sortFilter.options;
      sortOptions.forEach((item) => {
        if (item.name === sortFilter.optionSelected && item.filter && section) {
          let pageSort = item.filter.find((item) => item.pages.includes(section));
          if (pageSort) {
            sort = pageSort.property;
          }
        }
      });
    }

    if (sortModeFilter) {
      let sortModeOptions = sortModeFilter.options;
      sortModeOptions.forEach((item) => {
        if (item.name === sortModeFilter.optionSelected) {
          sortMode = item.filterValue;
        }
      });
      if (list === null && activeSearch) {
        sortModeFilter.optionSelected = 'Most influential';
      }
    }
    //compile unread and search
    if (unreadFilter) {
      unread = unreadFilter.value;
      if (unreadFilter.value && activeSearch) {
        phrase.push('unread only');
      }
    }

    if (searchFilter && searchFilter.value !== '' && (activeSearch || activeReference)) {
      searchWithin = {
        searchWithinQuery: ChangeSearchQuery(searchFilter.value),
      };
      phrase.push(`‘${searchFilter.value}’`);
    }

    toggleFiltersAll.forEach((toggleFilter) => {
      if (toggleFilter.value && toggleFilter.filterName) {
        filters.push({
          field: toggleFilter.filterName,
          value: toggleFilter.filterValue ?? 'false',
          operator: toggleFilter.operator ?? 'str_eq',
          hidden: toggleFilter.hidden ?? false,
        });
        if (toggleFilter.phrase) {
          phrase.push(toggleFilter.phrase);
        }
      }
    });

    //compile parliamentary record filters
    if (parliamentaryFilters) {
      const { options, filterName } = parliamentaryFilters;
      options.forEach((option) => {
        const { selectedOptions, selected, options, houseFilter, houseFilterSelectedOptions } = option;
        if (selected) {
          //PUSH PLAIN CONTENT
          selectedOptions.forEach((selectedOption) => {
            let contentOption = options.find((item) => item.name === selectedOption);
            const nestedOpts = contentOption?.nestedOptions ?? contentOption?.options;
            const nestedSelected = contentOption?.selectedNestedOptions;
            if (nestedOpts?.length) {
              if (nestedSelected?.length && nestedSelected.length !== nestedOpts.length) {
                filters.push({
                  field: filterName,
                  value: contentOption.filterValue,
                  operator: 'str_eq',
                });
                nestedSelected.forEach((nestedName) => {
                  const nestedItem = nestedOpts.find((n) => n.name === nestedName);
                  if (nestedItem?.filter) {
                    filters.push(nestedItem.filter);
                  }
                });
              } else {
                filters.push({
                  field: filterName,
                  value: contentOption.filterValue,
                  operator: 'str_eq',
                });
              }
            } else {
              filters.push({
                field: filterName,
                value: contentOption.filterValue,
                operator: 'str_eq',
              });
            }
          });

          //HOUSES
          if (houseFilter) {
            if (houseFilterSelectedOptions.length === 0) {
              houseFilter.options.forEach((houseOption) => {
                filters.push({
                  field: houseFilter.filterName,
                  value: houseOption.filterValue,
                  operator: 'str_eq',
                });
              });
            } else {
              houseFilterSelectedOptions.forEach((houseOption) => {
                let houseSelected = houseFilter.options.find((item) => item.name === houseOption);
                filters.push({
                  field: houseFilter.filterName,
                  value: houseSelected.filterValue,
                  operator: 'str_eq',
                });
              });
            }
          } else {
            filters.push({ field: 'house', value: 'n/a', operator: 'str_eq' });
          }
        }
      });
    }
    //topicPickerFilters
    if (topicPickerFilter) {
      const { filterName, options, sentiment } = topicPickerFilter;
      if (options?.length > 0) {
        const topicFilters = options?.map((item) => {
          return {
            field: `${filterName}${sentiment ? uppercaseFirstLetter(sentiment) : '12mo'}`,
            value: `${item?.id}`,
            operator: 'str_eq',
          };
        });
        filters = [...filters, ...topicFilters];
      }
    }

    //compileFilters
    filtersToCompile.forEach((item) => {
      const { filterName, options, additionalFilter, notSendIfOptionsAreSelected } = item;
      const allOptionsAreSelectedCondition =
        notSendIfOptionsAreSelected && options.filter((item) => item.selected).length === options.length;
      options.forEach((option) => {
        const { selected, filterValue, name, suboptions, optionSelected, operator, filter } = option;
        if (selected && !allOptionsAreSelectedCondition) {
          // Check if option has a direct filter array property
          if (filter && Array.isArray(filter)) {
            filter.forEach((filterItem) => {
              if (
                typeof filterItem.value !== 'undefined' &&
                typeof filterItem.field !== 'undefined' &&
                typeof filterItem.operator !== 'undefined'
              ) {
                filters.push(filterItem);
              }
            });
          } else {
            let value = filterValue ?? name;
            if (Array.isArray(value)) {
              value.forEach((val) => {
                if (
                  typeof val.value !== 'undefined' &&
                  typeof val.field !== 'undefined' &&
                  typeof val.operator !== 'undefined'
                ) {
                  filters.push(val);
                } else {
                  filters.push({
                    field: filterName,
                    value: val,
                    operator: item?.operator ?? operator ?? 'str_eq',
                  });
                }
              });
            } else {
              filters.push({
                field: filterName,
                value: value,
                operator: item?.operator ?? operator ?? 'str_eq',
              });
            }
          }
          let subFilters = compileSuboptions({
            options,
            optionSelected,
            suboptions,
          });
          if (subFilters.length > 0) {
            subFilters.forEach((subfilter) => filters.push(subfilter));
          }
          if (additionalFilter) {
            filters.push(additionalFilter);
          }
        }
      });

      //PHRASE
      let selectedOptions = options.filter((item) => item.selected);
      if (selectedOptions.length > 0) {
        let firstItem = selectedOptions[0];
        let firstItemName = firstItem.phrase ? firstItem.phrase : firstItem.name;
        if (firstItem.optionSelected) {
          if (!firstItem.optionSelected.startsWith('All')) {
            let optionSelected = firstItem.suboptions.find((suboption) => suboption.name === firstItem.optionSelected);
            firstItemName = optionSelected.phrase ? optionSelected.phrase : optionSelected.name;
          }
        }
        if (selectedOptions.length === 1) {
          phrase.push(`${firstItemName}${firstItem.optionSelected ? ' only' : ''}`);
        } else if (selectedOptions.length > 1) {
          phrase.push(`${firstItemName} + ${selectedOptions.length - 1}`);
        }
      }
    });

    hash = setFilterHashes({
      sortFilter,
      unreadFilter,
      filtersToCompile,
      searchFilter,
      sortModeFilter,
      toggleFilters: toggleFiltersAll,
      dateFilter,
      parliamentaryFilters,
      nationsFilter,
      topicPickerFilter,
      singleSelectFilters,
    });

    let contentFilters = {
      sort,
      searchWithin,
      filters,
      unread,
      hash,
      sortMode,
      singleSelectValues,
      nationsFilter,
      regulationGroupByDocket: groupByDocketToggle ? groupByDocketToggle.value : true,
      phrase: phrase.length > 0 ? `(${phrase.join(', ')})` : '',
    };
    return contentFilters;
  };

  const compileSuboptions = ({ options, optionSelected, suboptions }) => {
    let filters = [];
    if (suboptions) {
      let suboptionSelected = suboptions.find((suboption) => optionSelected === suboption.name);
      let { filter } = suboptionSelected;
      if (filter) {
        filter.forEach((subOptionFilter) => {
          options.forEach((option) => {
            if (option.selected && subOptionFilter.contentType === option.filterValue) {
              filters.push(subOptionFilter.filter);
            }
          });
        });
      }
    }
    return filters;
  };

  const activeResultsConditional = (filter, filters) => {
    const { searchState, activePlanProperty } = filter;
    const topicId = getUrlParam('topic-id');
    const singleSelectValues = filters.find((option) => option.type === 'singleSelect');
    switch (searchState) {
      case 'documentTypeSearch':
        return singleSelectValues?.optionSelected === 'Document' && searchResultsSection;
      case 'list':
        return !!topicId && topicId !== 'all' && topicId !== 'none' && list !== 'noListsCreated';
      case 'freeSearch':
        return list === null && (activeSearch || activeReference);
      case 'inbox':
        return list !== null && inboxSection;
      case 'not-inbox':
        return list === null || !inboxSection;
      case 'referenceSection':
        return (
          (referenceSection && activeReference) ||
          (activeReference && searchResultsSection) ||
          (activeSearch && searchResultsSection)
        );
      case 'activeSearch':
        return activeSearch || activeReference;
      case 'allResults':
        return !activeSearch && !activeReference;
      case 'questionsCondition':
        let parliamentaryRecordFilters = filters?.find((item) => item.type === 'parliamentaryRecordMultipleHierarchy');
        let questionItem = parliamentaryRecordFilters?.options.find((option) =>
          option.selectedOptions.includes('Written Q&As')
        );
        let noFiltersOptions = parliamentaryRecordFilters?.options.filter((option) => !option.selected);
        return (
          parliamentaryRecordFilters &&
          (noFiltersOptions.length === parliamentaryRecordFilters.options.length || questionItem)
        );
      case 'sentimentSortModeCondition':
        let sortMode = filters.find((option) => option.type === 'sortMode');
        return (
          !!list?.id &&
          enableSentiment &&
          (!sortMode || (sortMode.optionSelected !== 'Most positive' && sortMode.optionSelected !== 'Most negative'))
        );
      case 'disabledPlanFieldOption':
        return !activePlan[activePlanProperty];
      case 'embargoStatusEnabledCondition':
        return !isParliamentarian && !!activePlan?.enableHandpickedContent;
      case 'embargoStatusDisabledCondition':
        return !isParliamentarian && !activePlan?.enableHandpickedContent;
      case 'SeneddOrHolyroodCondition':
        const locationConditional =
          location?.pathname?.startsWith('/people/stakeholder-mapping') ||
          location?.pathname?.startsWith('/policymakers') ||
          location?.pathname?.startsWith('/people/directory');
        let nationFilter = filters.find((option) => option.name === 'Nation');
        return (
          locationConditional &&
          (!nationFilter || nationFilter.optionSelected === ConfigData?.filters?.nation?.optionSelected)
        );
      case 'searchResultsSection':
        return searchResultsSection;
      case 'NoteAuthorCondition':
        return !location?.pathname?.includes('/notes/my-own');
      case 'newAgencyPlanCondition':
        return newAgencyPlan;
      default:
        return true;
    }
  };

  return { compileFiltersFunction, activeResultsConditional };
};

export default useCompileFiltersFunction;
